import { useMemo, useState } from 'react';
import { Alert, Button, Collapse, DatePicker, Dropdown, Empty, Input, Modal, Select, Skeleton, Space, Switch, Table, Tabs, Tag, Tooltip, message } from 'antd';
import { CheckOutlined, CopyOutlined, EditOutlined, ExclamationCircleOutlined, ExportOutlined, TeamOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { rememberTeamBoard, teamBoardState } from './teamBoardNav';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  confirmMyTeamCompanies,
  copyTeamFromSibling,
  endTeamAssignment,
  finishTeamTransition,
  getTeamCompanyCard,
  resolveTeamIssue,
  updateTeamAssignment,
} from '@/services/teamAssignments.service';
import AssignDialog from './AssignDialog';
import AssignmentChip from './AssignmentChip';
import ClientStatusDialog from './ClientStatusDialog';
import ContactsDialog from './ContactsDialog';
import ContactReviewDialog from './ContactReviewDialog';
import { ReviewTag } from './contactReviewParts';
import EinvoiceReminderCompanyPanel from './EinvoiceReminderCompanyPanel';
import { filterContactsBySections, mySectionsFor, readOnlyMyAreas, storeOnlyMyAreas } from './contactAreas';
import { CLIENT_STATUS_COLORS, CONTACT_SECTIONS, FUNCTIONS, isOverdue, personName } from './model';

const fmt = (d) => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '');

// Per person and role, how many of the given companies the function covers.
const summarize = (rows, fn, total) => {
  const map = new Map();
  rows
    .filter((a) => a.function === fn)
    .forEach((a) => {
      const key = `${a.employee}|${a.role}|${a.transition || ''}|${a.substitution ? 'S' : ''}`;
      const cur = map.get(key) || { ...a, companies: [], notes: [] };
      cur.companies.push(a.company);
      if (a.note && !cur.notes.includes(a.note)) cur.notes.push(a.note);
      map.set(key, cur);
    });
  return [...map.values()]
    .map((x) => ({ ...x, count: x.companies.length, total }))
    .sort((x, y) => (x.role === 'LEAD' ? 0 : 1) - (y.role === 'LEAD' ? 0 : 1));
};

/**
 * The HLB team on one legal entity (all its companies), with hand-overs,
 * helpers, history, client status and client contacts. Used inside the board
 * and on the company profile. What can be changed follows the server's
 * answer: managers change the team, assigned staff edit contacts and confirm.
 */
export default function TeamFirmCard({ companyId, onChanged, embedded = false, showCompanyLink = false, showBoardLink = false }) {
  const { t } = useTranslation(['teamAssignments', 'teamContacts']);
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [contactsOf, setContactsOf] = useState(null);
  const [reviewOf, setReviewOf] = useState(null);
  const [report, setReport] = useState(null);
  const [noteEdit, setNoteEdit] = useState(null);
  const [copySource, setCopySource] = useState({});
  // Per viewer, off by default: everyone sees all contacts until they turn it on.
  const [onlyMyAreas, setOnlyMyAreas] = useState(readOnlyMyAreas);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['team-card', companyId],
    queryFn: () => getTeamCompanyCard(companyId),
    enabled: !!companyId,
  });

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['team-board'] });
    // Contacts decide who gets the e-invoice reminder; its panel must show the new recipients.
    queryClient.invalidateQueries({ queryKey: ['einvoice-reminder-company'] });
    onChanged?.();
  };

  // Former colleagues only for the history lines; they are never offered or shown on the team.
  const peopleById = useMemo(() => new Map([...(data?.formerPeople || []), ...(data?.people || [])].map((p) => [p._id, p])), [data]);
  const companies = data?.companies || [];
  const ids = companies.map((c) => c._id);
  const rows = data?.assignments || [];
  const canManage = !!data?.canManage;
  const me = data?.actor?.employeeId;
  const primary =
    [...companies].sort(
      (a, b) => (a.mainCompany ? 1 : 0) - (b.mainCompany ? 1 : 0) || (b.companyAdmins?.length || 0) - (a.companyAdmins?.length || 0) || a.name.length - b.name.length
    )[0] || null;

  const act = useMutation({
    mutationFn: ({ kind, payload }) => {
      if (kind === 'finish') return finishTeamTransition(payload);
      if (kind === 'end') return endTeamAssignment(payload);
      if (kind === 'update') return updateTeamAssignment(payload);
      if (kind === 'confirm') return confirmMyTeamCompanies(payload);
      if (kind === 'copy') return copyTeamFromSibling(payload);
      return resolveTeamIssue(payload);
    },
    onSuccess: () => {
      message.success(t('messages.saved'));
      refresh();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  if (isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (error) return <Alert type="warning" showIcon message={error?.response?.data?.message || error.message} />;
  if (!companies.length) return <Empty description={t('card.nothing')} />;

  // Covering for an absent colleague is not "my client" to confirm.
  const myRows = me ? rows.filter((a) => a.employee === me && !a.substitution) : [];
  // Companies of the group with nobody on them while the others have people.
  const emptySiblings = companies.length > 1 && rows.length ? companies.filter((c) => !rows.some((a) => a.company === c._id)) : [];
  const staffedSiblings = companies.filter((c) => rows.some((a) => a.company === c._id));
  const myState = myRows.some((a) => a.confirmation === 'REPORTED' && !a.issueResolvedAt)
    ? 'REPORTED'
    : myRows.length && myRows.every((a) => a.confirmation === 'CONFIRMED')
      ? 'CONFIRMED'
      : null;
  const issues = rows.filter((a) => a.confirmation === 'REPORTED' && !a.issueResolvedAt);
  const issuesByPerson = [...new Map(issues.map((a) => [a.employee, a])).values()];

  const userToPerson = new Map((data.people || []).filter((p) => p.userId).map((p) => [p.userId, p._id]));
  const accessOnly = [
    ...new Set(
      companies.flatMap((c) =>
        (c.companyAdmins || []).map((u) => userToPerson.get(u)).filter((pid) => pid && !rows.some((a) => a.company === c._id && a.employee === pid))
      )
    ),
  ];

  const chipMenu = (entry, targetIds) => ({
    items: [
      { key: 'note', label: t('card.editNote') },
      { key: 'role', label: entry.role === 'LEAD' ? t('card.makeHelper') : t('card.makeLead') },
      ...(entry.transition ? [{ key: 'until', label: t('card.transitionDate') }] : []),
      { type: 'divider' },
      { key: 'end', danger: true, label: entry.transition === 'IN' ? t('card.cancelTransition') : t('card.remove') },
    ],
    onClick: ({ key }) => {
      const base = { companyIds: targetIds, function: entry.function, employeeId: entry.employee };
      if (key === 'end') {
        Modal.confirm({
          title: t('card.removeConfirm', { name: personName(peopleById.get(entry.employee)), fn: t(`functions.${entry.function}`) }),
          content: entry.grantedAccess ? t('card.removeAccessHint') : null,
          okText: t('card.remove'),
          cancelText: t('actions.cancel'),
          okButtonProps: { shape: 'round', danger: true },
          cancelButtonProps: { shape: 'round' },
          onOk: () => act.mutateAsync({ kind: 'end', payload: base }),
        });
      }
      if (key === 'role') act.mutate({ kind: 'update', payload: { ...base, role: entry.role === 'LEAD' ? 'HELPER' : 'LEAD' } });
      if (key === 'note') setNoteEdit({ base, value: entry.notes?.[0] || entry.note || '', field: 'note' });
      if (key === 'until')
        setNoteEdit({ base, value: entry.transitionUntil ? dayjs(entry.transitionUntil) : null, field: 'transitionUntil' });
    },
  });

  // One function row: who, and (for managers) what can be done.
  const functionRow = (fn, scopeCompanies) => {
    const scopeIds = scopeCompanies.map((c) => c._id);
    const scopeRows = rows.filter((a) => scopeIds.includes(a.company));
    const entries = summarize(scopeRows, fn, scopeIds.length);
    const hasLead = entries.some((e) => e.role === 'LEAD' && e.transition !== 'IN');
    const inTransition = entries.some((e) => e.transition);
    const outs = entries.filter((e) => e.transition === 'OUT');
    const ins = entries.filter((e) => e.transition === 'IN');
    // The client's facts for this work: bank and method for payments, headcount for payroll.
    const scopePrimary = scopeCompanies.find((c) => c._id === primary?._id) || scopeCompanies[0];
    const facts =
      fn === 'PAYMENTS'
        ? [
            scopePrimary?.hlbClient.paymentsBank ? `${t('card.bank')}: ${scopePrimary.hlbClient.paymentsBank}` : null,
            scopePrimary?.hlbClient.paymentsMode ? `${t('card.paymentsMode')}: ${scopePrimary.hlbClient.paymentsMode}` : null,
          ].filter(Boolean)
        : fn === 'PAYROLL'
          ? scopeCompanies.some((c) => c.hlbClient.payrollHeadcount != null)
            ? [
                t('card.headcount', { count: scopeCompanies.reduce((sum, c) => sum + (c.hlbClient.payrollHeadcount || 0), 0) }) +
                  (scopeCompanies.length > 1
                    ? ` (${scopeCompanies
                        .filter((c) => c.hlbClient.payrollHeadcount != null)
                        .map((c) => `${c.name}: ${c.hlbClient.payrollHeadcount}`)
                        .join(', ')})`
                    : ''),
                ...scopeCompanies.map((c) => c.hlbClient.payrollHeadcountNote).filter(Boolean),
              ]
            : []
          : [];
    return (
      <div key={fn} className="grid gap-2 border-b border-zinc-100 py-2 last:border-b-0 md:grid-cols-[150px_minmax(0,1fr)_auto] md:items-start">
        <div className="font-medium">{t(`functions.${fn}`)}</div>
        <div>
          {entries.length ? (
            <div className="flex flex-wrap items-center">
              {entries.map((e) =>
                canManage ? (
                  <Dropdown key={`${e.employee}${e.role}${e.transition}`} menu={chipMenu(e, e.companies)} trigger={['click']}>
                    <span style={{ cursor: 'pointer' }}>
                      <AssignmentChip entry={e} peopleById={peopleById} />
                    </span>
                  </Dropdown>
                ) : (
                  <AssignmentChip key={`${e.employee}${e.role}${e.transition}`} entry={e} peopleById={peopleById} />
                )
              )}
            </div>
          ) : (
            <span className="opacity-60">{t('card.nobody')}</span>
          )}
          {inTransition && outs.length > 0 && ins.length > 0 && (
            <div className="text-xs opacity-70">
              {t('card.transitionLine', {
                from: outs.map((e) => personName(peopleById.get(e.employee))).join(', '),
                to: ins.map((e) => personName(peopleById.get(e.employee))).join(', '),
              })}
              {outs[0]?.transitionUntil ? ` · ${t('chip.until', { date: dayjs(outs[0].transitionUntil).format('DD.MM.YYYY') })}` : ` · ${t('card.noDate')}`}
              {outs.some(isOverdue) && <span className="font-semibold text-red-600"> · {t('chip.overdue')}</span>}
            </div>
          )}
          {entries.flatMap((e) => e.notes || []).length > 0 && (
            <div className="text-xs opacity-60">{[...new Set(entries.flatMap((e) => e.notes || []))].join(' · ')}</div>
          )}
          {facts.length > 0 && <div className="text-xs opacity-80">{facts.join(' · ')}</div>}
        </div>
        {canManage && (
          <Space wrap size={6}>
            {inTransition && (
              <Button
                size="small"
                shape="round"
                type="primary"
                onClick={() => act.mutate({ kind: 'finish', payload: { companyIds: scopeIds, function: fn } })}
              >
                {t('card.finishTransition')}
              </Button>
            )}
            <Button size="small" shape="round" onClick={() => setDialog({ mode: hasLead ? 'transfer' : 'assign', fn, scope: scopeCompanies })}>
              {hasLead ? t('card.transfer') : t('card.assign')}
            </Button>
            <Button size="small" shape="round" onClick={() => setDialog({ mode: 'help', fn, scope: scopeCompanies })}>
              {t('card.addHelper')}
            </Button>
          </Space>
        )}
      </div>
    );
  };

  // History: every assignment that started or ended, newest first.
  const events = [
    ...rows.map((a) => ({ at: a.startedAt, a, kind: 'start' })),
    ...(data.history || []).flatMap((a) => [
      { at: a.endedAt, a, kind: 'end' },
      { at: a.startedAt, a, kind: 'start' },
    ]),
  ]
    .filter((e) => e.at)
    .sort((x, y) => new Date(y.at) - new Date(x.at))
    .slice(0, 80);
  const companyName = (id) => companies.find((c) => c._id === id)?.name || '';

  const statusBlock = (
    <div className="flex flex-wrap items-center gap-2">
      {primary && (
        <Tag color={CLIENT_STATUS_COLORS[primary.hlbClient.status]}>{t(`clientStatuses.${primary.hlbClient.status}`)}</Tag>
      )}
      {[...new Set(companies.flatMap((c) => c.hlbClient.flags))].map((f) => (
        <Tag key={f} color={f === 'CLOSING' ? 'red' : 'default'}>
          {t(`flags.${f}`)}
        </Tag>
      ))}
      {primary?.hlbClient.shortName && primary.fullName && primary.fullName !== primary.name && (
        <span className="text-xs opacity-70">
          {t('card.fullName')}: {primary.fullName}
        </span>
      )}
      {canManage && (
        <Button size="small" shape="round" icon={<EditOutlined />} onClick={() => setStatusOpen(true)}>
          {t('card.editClient')}
        </Button>
      )}
      <span className="ml-auto flex flex-wrap gap-2">
        {showCompanyLink && primary && (
          <Link
            to={`${ROUTES.admin}/${ROUTES.companies}/${primary._id}`}
            state={teamBoardState()}
            onClick={rememberTeamBoard}
          >
            <Button size="small" shape="round" icon={<ExportOutlined />}>
              {t('links.openCompany')}
            </Button>
          </Link>
        )}
        {showBoardLink && (
          <Link to={`${ROUTES.admin}/${ROUTES.teamAssignments}?company=${companyId}`}>
            <Button size="small" shape="round" icon={<TeamOutlined />}>
              {t('links.openBoard')}
            </Button>
          </Link>
        )}
      </span>
    </div>
  );

  // Addresses of one section, ready to paste into a mail client.
  const copySection = async (c, section, delivery) => {
    const list = c.clientContacts
      .filter((x) => x.email && !x.inactive && x.sections.some((y) => y.section === section && (y.delivery || 'TO') === delivery))
      .map((x) => x.email);
    try {
      await navigator.clipboard.writeText(list.join('; '));
      message.success(t('contacts.copied', { count: list.length }));
    } catch {
      message.error(t('contacts.copyFailed'));
    }
  };
  const copyButtons = (c) => {
    const items = CONTACT_SECTIONS.flatMap((section) =>
      ['TO', 'CC']
        .map((delivery) => ({
          section,
          delivery,
          count: c.clientContacts.filter((x) => x.email && !x.inactive && x.sections.some((y) => y.section === section && (y.delivery || 'TO') === delivery)).length,
        }))
        .filter((x) => x.count)
    );
    if (!items.length) return null;
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs opacity-60">{t('contacts.copyLabel')}</span>
        {items.map((x) => (
          <Button key={`${x.section}${x.delivery}`} size="small" shape="round" icon={<CopyOutlined />} onClick={() => copySection(c, x.section, x.delivery)}>
            {t(`sections.${x.section}`)}
            {x.delivery === 'CC' ? ` · ${t('delivery.CC')}` : ''} ({x.count})
          </Button>
        ))}
      </div>
    );
  };

  const toggleOnlyMyAreas = (on) => {
    setOnlyMyAreas(on);
    storeOnlyMyAreas(on);
  };

  // Staff only: managers always see every contact. Filters the list, never the editor.
  const myAreasSwitch = (mySections) =>
    !canManage && me ? (
      <Tooltip title={mySections ? null : t('contacts.onlyMyAreasAll')}>
        <label className="flex w-fit items-center gap-2 text-sm">
          <Switch size="small" checked={onlyMyAreas && !!mySections} disabled={!mySections} onChange={toggleOnlyMyAreas} />
          {t('contacts.onlyMyAreas')}
        </label>
      </Tooltip>
    ) : null;

  const contactsTable = (c) => {
    const mySections = mySectionsFor({ rows, me, companyId: c._id, canManage });
    const shown = onlyMyAreas ? filterContactsBySections(c.clientContacts, mySections) : c.clientContacts;
    return (
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <ReviewTag label={t('review.hlb', { ns: 'teamContacts' })} status={c.contactsReview?.status} by={c.contactsReview?.confirmedBy} at={c.contactsReview?.confirmedAt} />
          <ReviewTag label={t('review.client', { ns: 'teamContacts' })} status={c.contactsReview?.clientStatus} by={c.contactsReview?.clientConfirmedBy} at={c.contactsReview?.clientConfirmedAt} />
          <Button size="small" shape="round" onClick={() => setReviewOf(c._id)}>
            {t('review.open', { ns: 'teamContacts' })}
          </Button>
        </div>
        {copyButtons(c)}
        <EinvoiceReminderCompanyPanel companyId={c._id} companyName={c.name} />
        {c.clientContacts.length > 0 && myAreasSwitch(mySections)}
        {shown.length !== c.clientContacts.length && (
          <span className="text-xs opacity-60">{t('contacts.shownOf', { shown: shown.length, total: c.clientContacts.length })}</span>
        )}
        {shown.length ? (
          <Table
            size="small"
            rowKey="_id"
            bordered
            pagination={false}
            dataSource={shown}
            scroll={{ x: 640 }}
            rowClassName={(r) => (r.inactive ? 'opacity-50 line-through' : '')}
            columns={[
              {
                title: t('contacts.name'),
                dataIndex: 'name',
                render: (v, r) => (
                  <span>
                    {v || '—'}
                    {r.position ? <span className="opacity-60"> · {r.position}</span> : null}
                    {r.inactive && <Tag className="ml-1">{t('contacts.inactive')}</Tag>}
                  </span>
                ),
              },
              { title: t('contacts.email'), dataIndex: 'email', render: (v) => (v ? <a href={`mailto:${v}`}>{v}</a> : '—') },
              { title: t('contacts.phone'), dataIndex: 'phone', render: (v) => v || '—' },
              {
                title: t('contacts.sections'),
                dataIndex: 'sections',
                render: (s) =>
                  (s || []).map((x) => (
                    <Tag key={x.section} style={{ borderStyle: x.delivery === 'CC' ? 'dashed' : 'solid' }}>
                      {t(`sections.${x.section}`)}
                      {x.delivery === 'CC' ? ` · ${t('delivery.CC')}` : ''}
                    </Tag>
                  )),
              },
              {
                title: t('contacts.updated'),
                dataIndex: 'updatedAt',
                render: (v, r) => <span className="text-xs opacity-70">{[r.updatedBy, v ? dayjs(v).format('DD.MM.YYYY') : ''].filter(Boolean).join(' · ')}</span>,
              },
            ]}
          />
        ) : (
          <span className="opacity-60">{c.clientContacts.length ? t('contacts.noneInMyAreas') : t('contacts.none')}</span>
        )}
        {c.canEditContacts && (
          <Button size="small" shape="round" icon={<EditOutlined />} style={{ width: 'fit-content' }} onClick={() => setContactsOf(c)}>
            {t('contacts.edit')}
          </Button>
        )}
      </div>
    );
  };

  const contactCompanies = companies.filter((c) => c.canEditContacts || c.clientContacts.length);
  const startCompany = data.requestedCompanyId || primary?._id;

  return (
    <div className={`grid gap-4 ${embedded ? '' : 'rounded-xl border border-zinc-200 p-4'}`}>
      {statusBlock}
      {companies.some((c) => c.hlbClient.note) && (
        <div className="text-sm opacity-80">{[...new Set(companies.map((c) => c.hlbClient.note).filter(Boolean))].join(' · ')}</div>
      )}

      {me && myRows.length > 0 && (
        <Alert
          type={myState === 'CONFIRMED' ? 'success' : myState === 'REPORTED' ? 'warning' : 'info'}
          showIcon
          message={
            myState === 'CONFIRMED' ? t('confirm.confirmed') : myState === 'REPORTED' ? t('confirm.reported') : t('confirm.question')
          }
          action={
            <Space wrap>
              {myState !== 'CONFIRMED' && (
                <Button size="small" shape="round" type="primary" icon={<CheckOutlined />} onClick={() => act.mutate({ kind: 'confirm', payload: { companyIds: ids, status: 'CONFIRMED' } })}>
                  {t('confirm.yes')}
                </Button>
              )}
              <Button size="small" shape="round" icon={<ExclamationCircleOutlined />} onClick={() => setReport({ text: '' })}>
                {t('confirm.report')}
              </Button>
            </Space>
          }
        />
      )}

      {canManage && issuesByPerson.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t('issues.title')}
          description={
            <div className="grid gap-1">
              {issuesByPerson.map((a) => (
                <div key={a.employee} className="flex flex-wrap items-center gap-2">
                  <b>{personName(peopleById.get(a.employee))}:</b>
                  <span>{a.confirmationNote}</span>
                  <Button size="small" shape="round" onClick={() => act.mutate({ kind: 'resolve', payload: { companyId: companies.find((c) => rows.some((r) => r.company === c._id && r.employee === a.employee))?._id, employeeId: a.employee } })}>
                    {t('issues.resolve')}
                  </Button>
                </div>
              ))}
            </div>
          }
        />
      )}

      {canManage && emptySiblings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t('group.emptyTitle', { count: emptySiblings.length })}
          description={
            <div className="grid gap-2">
              <div className="text-xs opacity-80">{t('group.emptyHint')}</div>
              {emptySiblings.map((c) => {
                const from = copySource[c._id] || primary?._id;
                const fromId = staffedSiblings.some((x) => x._id === from) ? from : staffedSiblings[0]?._id;
                return (
                  <div key={c._id} className="flex flex-wrap items-center gap-2">
                    <b>{c.name}</b>
                    <span className="text-xs opacity-70">{t('group.takeFrom')}</span>
                    <Select
                      size="small"
                      style={{ minWidth: 220 }}
                      value={fromId}
                      onChange={(v) => setCopySource((m) => ({ ...m, [c._id]: v }))}
                      options={staffedSiblings.map((x) => ({ value: x._id, label: x.name }))}
                    />
                    <Button
                      size="small"
                      shape="round"
                      type="primary"
                      disabled={!fromId}
                      loading={act.isPending}
                      onClick={() => act.mutate({ kind: 'copy', payload: { companyId: c._id, fromCompanyId: fromId } })}
                    >
                      {t('group.copy')}
                    </Button>
                  </div>
                );
              })}
            </div>
          }
        />
      )}

      <div>
        <h4 className="mb-1 text-sm font-semibold opacity-70">{t('card.team')}</h4>
        {FUNCTIONS.map((fn) => functionRow(fn, companies))}
      </div>

      {canManage && accessOnly.length > 0 && (
        <div className="text-sm">
          <b>{t('card.accessOnly')}:</b> {accessOnly.map((pid) => personName(peopleById.get(pid))).join(', ')}
          <div className="text-xs opacity-60">{t('card.accessOnlyHint')}</div>
        </div>
      )}

      {/* min-w-0: as a grid item the list may shrink below its tabs' width, so the
          tabs of a firm with many records scroll instead of widening the card. */}
      <Collapse
        className="min-w-0"
        size="small"
        items={[
          ...(companies.length > 1
            ? [
                {
                  key: 'byCompany',
                  label: t('card.byCompany', { count: companies.length }),
                  children: (
                    <div className="grid gap-3">
                      {companies.map((c) => (
                        <div key={c._id} className="rounded-lg border border-zinc-100 p-2">
                          <div className="mb-1 font-medium">{c.name}</div>
                          {FUNCTIONS.map((fn) => functionRow(fn, [c]))}
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]
            : []),
          {
            key: 'contacts',
            label: t('contacts.title', { count: companies.reduce((s, c) => s + c.clientContacts.length, 0) }),
            children:
              companies.length > 1 ? (
                <Tabs
                  size="small"
                  defaultActiveKey={startCompany}
                  items={(contactCompanies.length ? contactCompanies : companies).map((c) => ({
                    key: c._id,
                    label: `${c.name}${c.clientContacts.length ? ` (${c.clientContacts.length})` : ''}`,
                    children: contactsTable(c),
                  }))}
                />
              ) : (
                contactsTable(companies[0])
              ),
          },
          {
            key: 'history',
            label: t('card.history'),
            children: events.length ? (
              <ul className="grid gap-1 text-sm">
                {events.map((e, i) => (
                  <li key={`${e.a._id}-${e.kind}-${i}`} className="border-l-2 border-zinc-200 pl-2">
                    <span className="font-mono text-xs opacity-60">{fmt(e.at)}</span>{' '}
                    {t(`history.${e.kind}`, {
                      fn: t(`functions.${e.a.function}`),
                      name: personName(peopleById.get(e.a.employee)),
                      role: t(`roles.${e.a.role}`),
                    })}
                    {e.kind === 'end' && e.a.endReason ? ` (${t(`endReasons.${e.a.endReason}`, { defaultValue: e.a.endReason })})` : ''}
                    {companies.length > 1 ? <span className="opacity-60"> · {companyName(e.a.company)}</span> : null}
                    {(e.kind === 'end' ? e.a.endedBy : e.a.createdBy) && data.actors[e.kind === 'end' ? e.a.endedBy : e.a.createdBy] ? (
                      <span className="opacity-60"> · {data.actors[e.kind === 'end' ? e.a.endedBy : e.a.createdBy]}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="opacity-60">{t('card.noHistory')}</span>
            ),
          },
        ]}
      />

      {dialog && (
        <AssignDialog
          open
          mode={dialog.mode}
          fn={dialog.fn}
          companies={dialog.scope}
          rows={rows}
          people={data.people}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      )}
      <ClientStatusDialog
        open={statusOpen}
        companies={companies}
        primaryId={primary?._id}
        hasAssignments={rows.length > 0}
        onClose={() => setStatusOpen(false)}
        onDone={refresh}
      />
      <ContactsDialog open={!!contactsOf} company={contactsOf} onClose={() => setContactsOf(null)} onDone={refresh} />
      <ContactReviewDialog companyId={reviewOf} onClose={() => setReviewOf(null)} onChanged={refresh} />
      <Modal
        open={!!report}
        title={t('confirm.reportTitle')}
        onCancel={() => setReport(null)}
        onOk={() =>
          act.mutateAsync({ kind: 'confirm', payload: { companyIds: ids, status: 'REPORTED', note: report.text } }).then(() => setReport(null))
        }
        okText={t('confirm.send')}
        cancelText={t('actions.cancel')}
        okButtonProps={{ shape: 'round', disabled: !report?.text?.trim() }}
        cancelButtonProps={{ shape: 'round' }}
      >
        <Input.TextArea
          rows={3}
          value={report?.text}
          onChange={(e) => setReport({ text: e.target.value })}
          placeholder={t('confirm.reportPlaceholder')}
        />
      </Modal>
      <Modal
        open={!!noteEdit}
        title={noteEdit?.field === 'transitionUntil' ? t('card.transitionDate') : t('card.editNote')}
        onCancel={() => setNoteEdit(null)}
        onOk={() => {
          const v = noteEdit.value;
          const payload =
            noteEdit.field === 'transitionUntil'
              ? { ...noteEdit.base, transitionUntil: v ? v.format('YYYY-MM-DD') : null }
              : { ...noteEdit.base, note: v };
          return act.mutateAsync({ kind: 'update', payload }).then(() => setNoteEdit(null));
        }}
        okText={t('actions.save')}
        cancelText={t('actions.cancel')}
        okButtonProps={{ shape: 'round' }}
        cancelButtonProps={{ shape: 'round' }}
      >
        {noteEdit?.field === 'transitionUntil' ? (
          <DatePicker
            format="DD.MM.YYYY"
            value={noteEdit.value}
            onChange={(v) => setNoteEdit((s) => ({ ...s, value: v }))}
          />
        ) : (
          <Input
            value={noteEdit?.value}
            maxLength={500}
            onChange={(e) => setNoteEdit((s) => ({ ...s, value: e.target.value }))}
          />
        )}
      </Modal>
    </div>
  );
}

